// The seller deal's buyer board (window.BuyerEngagementsSection) and its
// pursuit-stage / pass-reason cells, moved out of deal_detail.jsx. The
// pursuit-stage helpers and EditableCell stay owned by deal_detail.jsx and
// are read here through window.*.

// ============ Buyer engagements section ============
const NDA_HUE = { not_sent: 'slate', sent: 'amber', redlined: 'amber', signed: 'green', declined: 'rose' };

// Roster column widths, shared by the header and the body rows so a narrow
// window can't shear them apart. Buyer takes the slack; the rest are fixed and
// never shrink — below MIN_W the whole table scrolls sideways instead of
// squeezing pills into their neighbours' cells.
// pursuit fits the longest DealCloud stage label in full ("On Hold - High
// Priority" measures 131px as a pill) plus the ↗ open button, so the common
// case never ellipsises; interest fits "not interested" under the pill.
const BE_COL = { pursuit: 184, blast: 110, nda: 98, interest: 140, added: 90, bsCall: 88 };
const BE_BUYER_MIN = 156;
const BE_MIN_W = BE_BUYER_MIN + Object.values(BE_COL).reduce((a, b) => a + b, 0);

// Timestamps on the roster are ISO; render them compactly ("Mar 4" this year,
// "Mar 4 '25" otherwise) with the full date left to the cell's tooltip.
const fmtRosterDate = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d)) return null;
  const sameYear = d.getFullYear() === new Date().getFullYear();
  const base = d.toLocaleDateString([], { month: 'short', day: 'numeric' });
  return sameYear ? base : `${base} '${String(d.getFullYear()).slice(2)}`;
};
const fmtRosterDateLong = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  return isNaN(d) ? null : d.toLocaleDateString([], { month: 'long', day: 'numeric', year: 'numeric' });
};

// A buyer's pursuit stage on one seller deal, editable in place. Shared by
// the deal page's buyer board and the company page's Interested buyers rail
// (window.PursuitStageCell). The save goes through the roster row, which
// also opens the pursuit record when the buyer has none yet (a buyer Atlas
// added via Find buyers / Send to deal). `onSaved` gets the fresh roster row
// (pursuit stage + id, interest). ↗ opens the pursuit record.
// Its own button + Combobox rather than EditableCell: EditableCell's read
// view pulls left 4px, and inside a shrink-to-fit parent (this cell sits
// inline in the company rail) Chrome sizes it 4px short and ellipsises the
// label — the same squeeze that cut the pass reason to "reas…".
const PursuitStageCell = ({ b, dealName, editable = true, onSaved, maxW }) => {
  const { PURSUIT_STAGE_OPTIONS, pursuitKeyFor, cleanDcStage, dcStageHue } = window;
  const [picking, setPicking] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const hasPursuit = !!b.pursuit_engagement_id;
  const who = b.buyer_name || 'this buyer';
  const current = pursuitKeyFor(b.pursuit_stage);
  const save = async (stage) => {
    if (!stage || (hasPursuit && stage === current)) return;
    if (!hasPursuit) {
      const label = (PURSUIT_STAGE_OPTIONS.find((o) => o.value === stage) || {}).label || stage;
      const ok = await showConfirm({
        title: `Open a pursuit for ${who}?`,
        body: `${who} has no buyer engagement on ${dealName || 'this deal'} yet. This creates one at “${label}” — in DealCloud too.`,
        confirmLabel: 'Create pursuit',
      });
      if (!ok) return;
    }
    setSaving(true);
    try {
      const row = await window.AtlasApi.setBuyerPursuitStage(b.id, stage);
      onSaved && onSaved(row);
      if (row && row.pursuit_created) {
        pushToast({ type: 'success', title: 'Pursuit opened', body: `${who} › ${dealName || 'this deal'} — queued to DealCloud.` });
      }
    } catch (e) {
      pushToast({ type: 'error', title: 'Could not move the pursuit', body: e.message });
    } finally {
      setSaving(false);
    }
  };
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2, minWidth: 0, maxWidth: '100%' }}>
      {picking ? (
        <Combobox autoOpen dense value={current || ''} options={PURSUIT_STAGE_OPTIONS} menuMinWidth={210}
          style={{ width: 150 }} onClose={() => setPicking(false)}
          onChange={(e) => { setPicking(false); save(e.target.value); }} />
      ) : (
        <button disabled={!editable || saving} onClick={() => setPicking(true)}
          title={editable ? `${b.pursuit_stage ? `Pursuit stage: ${cleanDcStage(b.pursuit_stage)}` : `No buyer engagement on ${dealName || 'this deal'} yet`} — click to change` : undefined}
          onMouseOver={(e) => { if (editable) e.currentTarget.style.background = 'var(--surface-hover)'; }}
          onMouseOut={(e) => { e.currentTarget.style.background = 'transparent'; }}
          style={{
            display: 'inline-flex', alignItems: 'center', minWidth: 0, maxWidth: '100%',
            padding: '2px 4px', marginLeft: -4, borderRadius: 4, border: 'none', background: 'transparent',
            cursor: editable ? 'pointer' : 'default', fontFamily: 'inherit', fontSize: 11,
            color: 'var(--text-4)', whiteSpace: 'nowrap', ...(window.phoneHitHost ? window.phoneHitHost() : null),
          }}>
          {window.PhoneHit && <window.PhoneHit />}
          {b.pursuit_stage
            ? <Pill hue={dcStageHue(b.pursuit_stage)} sz="sm" maxW={maxW}>{cleanDcStage(b.pursuit_stage)}</Pill>
            : (hasPursuit ? '—' : 'No pursuit')}
          {saving && <span style={{ marginLeft: 4, fontSize: 10 }}>saving…</span>}
        </button>
      )}
      {hasPursuit && !picking && (
        <button
          onClick={(e) => { e.stopPropagation(); window.AtlasNav?.goToDeal?.(b.pursuit_engagement_id); }}
          title={`Open buyer engagement: ${who} › ${dealName || 'this deal'}`}
          onMouseOver={(e) => { e.currentTarget.style.background = 'var(--surface-2)'; e.currentTarget.style.color = 'var(--accent-text)'; }}
          onMouseOut={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-4)'; }}
          // Phone: 14px more from the stage pill, so this 44px box doesn't take the pill's taps.
          style={{ flexShrink: 0, background: 'transparent', border: 'none', borderRadius: 4, cursor: 'pointer', padding: '1px 4px', color: 'var(--text-4)', fontSize: 11, lineHeight: 1, ...(window.phoneHitHost ? window.phoneHitHost() : null),
                   ...(window.AtlasDevice && window.AtlasDevice.phone ? { marginLeft: 14 } : null) }}
        >{window.PhoneHit && <window.PhoneHit />}↗</button>
      )}
    </span>
  );
};

// Why a buyer passed. "other" asks for the reason as a line of text
// (pass_detail) right in the cell — re-pick "other…" to change it.
const PASS_REASONS = [
  { value: 'not_interested', label: 'not interested' },
  { value: 'too_small',      label: 'too small' },
  { value: 'geography',      label: 'geography' },
  { value: 'valuation',      label: 'valuation' },
  { value: 'wrong_industry', label: 'wrong industry' },
  { value: 'other',          label: 'other…', description: 'Type the reason' },
];
const PassReasonCell = ({ b, onPatch }) => {
  const [mode, setMode] = React.useState(null);  // null | 'pick' | 'type'
  const [text, setText] = React.useState('');
  const editable = !!onPatch;
  // An onPatch that already toasted its failure (the Buyers workspace's) marks the error `toasted`.
  const save = (patch) => Promise.resolve(onPatch(b.id, patch))
    .catch((e) => { if (!(e && e.toasted)) pushToast({ type: 'error', title: 'Could not save the reason', body: e.message }); });

  if (mode === 'type') {
    const done = (commit) => {
      setMode(null);
      if (commit) save({ pass_reason: 'other', pass_detail: text.trim() || null });
    };
    return (
      <input autoFocus value={text} maxLength={200} placeholder="Why did they pass?"
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') done(true); if (e.key === 'Escape') { e.stopPropagation(); done(false); } }}
        onBlur={() => done(true)}
        style={{
          width: '100%', padding: '2px 6px', fontSize: 11, fontFamily: 'inherit', outline: 'none',
          border: '1px solid var(--accent)', borderRadius: 4, background: 'var(--surface)', color: 'var(--text)',
        }} />
    );
  }
  if (mode === 'pick') {
    const opts = b.pass_reason ? [...PASS_REASONS, { value: '', label: '— clear —' }] : PASS_REASONS;
    return (
      <Combobox autoOpen dense value={b.pass_reason || ''} options={opts} menuMinWidth={170}
        style={{ width: '100%' }} onClose={() => setMode((m) => (m === 'pick' ? null : m))}
        onChange={(e) => {
          const v = e.target.value;
          if (v === 'other') { setText(b.pass_reason === 'other' ? (b.pass_detail || '') : ''); setMode('type'); return; }
          setMode(null);
          // Leaving "other" drops its typed text with it.
          if (v !== (b.pass_reason || '')) save({ pass_reason: v || null, ...(b.pass_reason === 'other' ? { pass_detail: null } : {}) });
        }} />
    );
  }
  const reasonLabel = b.pass_reason === 'other'
    ? (b.pass_detail ? `other: ${b.pass_detail}` : 'other')
    : ((PASS_REASONS.find((o) => o.value === b.pass_reason) || {}).label || (b.pass_reason || '').replace(/_/g, ' '));
  return (
    <button disabled={!editable} onClick={() => setMode('pick')}
      title={b.pass_reason ? `Pass reason: ${reasonLabel}${b.pass_detail && b.pass_reason !== 'other' ? `\n${b.pass_detail}` : ''}` : 'Why did they pass?'}
      onMouseOver={(e) => { if (editable) e.currentTarget.style.background = 'var(--surface-hover)'; }}
      onMouseOut={(e) => { e.currentTarget.style.background = 'transparent'; }}
      style={{
        // Typed "other" reasons wrap to two lines before clipping; the
        // tooltip carries the whole thing.
        display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
        maxWidth: '100%', padding: '1px 5px', marginLeft: -5, borderRadius: 4,
        border: '1px solid transparent', background: 'transparent', cursor: editable ? 'pointer' : 'default',
        fontSize: 10.5, lineHeight: 1.35, fontFamily: 'inherit', textAlign: 'left',
        color: b.pass_reason ? 'var(--text-3)' : 'var(--text-4)',
        overflow: 'hidden', overflowWrap: 'anywhere',
      }}>
      {b.pass_reason ? reasonLabel : '+ reason'}
    </button>
  );
};

// Board filter: who's still in play (passed = not interested) × NDA signed.
// Remembered per viewer, across deals.
const BE_FILTER_KEY = 'atlas.buyerBoardFilter';
const BE_STATUS_FILTERS = [
  ['all', 'All', null],
  ['live', 'Live', (b) => b.interest_level !== 'passed'],
  ['passed', 'Passed', (b) => b.interest_level === 'passed'],
];
const beNdaSigned = (b) => b.nda_status === 'signed';
const BeChip = ({ on, onClick, label, count }) => (
  <button onClick={onClick} style={{
    padding: '3px 9px', fontSize: 11.5, borderRadius: 999, cursor: 'pointer', fontFamily: 'inherit',
    border: `1px solid ${on ? 'var(--accent)' : 'var(--border)'}`,
    background: on ? 'var(--accent-soft)' : 'var(--surface)',
    color: on ? 'var(--accent-text)' : 'var(--text-2)', fontWeight: on ? 600 : 500,
  }}>{label} <span className="num" style={{ color: 'var(--text-4)' }}>{count}</span></button>
);

// The latest buyer search for a deal (undefined until the list answers, null
// for none), re-polled while it runs so Find buyers lands on View results. The
// poll is usePolling: a failed or unchanged tick still schedules the next one.
const useLastBuyerSearch = (dealId, on) => {
  const [last, setLast] = React.useState(undefined);
  const load = React.useCallback(() => {
    if (!dealId || !on || !window.AtlasApi.listBuyerSearches) return Promise.resolve(null);
    return window.AtlasApi.listBuyerSearches(dealId)
      .then((r) => { const it = (r && r.items && r.items[0]) || null; setLast(it); return it; })
      .catch((e) => { setLast((cur) => (cur === undefined ? null : cur)); throw e; });
  }, [dealId, on]);
  React.useEffect(() => { load().catch(() => {}); }, [load]);
  window.usePolling(load, {
    active: !!last && ['queued', 'running'].includes(last.status), key: dealId, first: 5000, giveUpAfter: 0,
    signature: (it) => (it ? `${it.id}:${it.status}:${it.step || ''}` : ''),
  });
  return [last, load];
};

const BuyerEngagementsSection = ({ deal, buyerEngs, onPatch, onRowUpdate, onBuyersAdded }) => {
  const [searchOpen, setSearchOpen] = React.useState(false);
  const [addOpen, setAddOpen] = React.useState(false);
  const [filter, setFilterState] = React.useState(() => {
    try {
      const s = JSON.parse(localStorage.getItem(BE_FILTER_KEY) || 'null');
      if (s && BE_STATUS_FILTERS.some(([k]) => k === s.status)) return { status: s.status, nda: !!s.nda };
    } catch (_) {}
    return { status: 'all', nda: false };
  });
  const setFilter = (patch) => setFilterState((f) => {
    const next = { ...f, ...patch };
    try { localStorage.setItem(BE_FILTER_KEY, JSON.stringify(next)); } catch (_) {}
    return next;
  });
  // Buyer-side engagements have no interested-buyer list of their own — hide.
  // Seller-side engagements keep the section visible even at zero so the
  // buyer board is discoverable before the first blast response lands.
  const sellerSide = deal?.type !== 'buyer_buyside';
  // The latest buyer search for this deal, so a finished run is visible on
  // the page itself and not only inside the drawer (Andres, Swann
  // Technologies, 2026-09-23: "I don't see them").
  const [lastSearch, loadLastSearch] = useLastBuyerSearch(deal && deal.id, sellerSide && !window.AtlasIsGuest);
  if (buyerEngs === null) return null; // still loading — silent
  if (buyerEngs.length === 0 && !sellerSide) return null;

  // "Find buyers" runs Samuel's buyer search on this deal (app/buyer_search):
  // seller read → roster ranking → model re-rank → who to call. The drawer
  // lives in buyers_gpp.jsx (window.BuyerSearchDrawer) next to the matcher.
  const searchDone = lastSearch && lastSearch.status === 'done';
  const searchWhen = lastSearch && lastSearch.finished_at
    ? new Date(lastSearch.finished_at).toLocaleDateString([], { month: 'short', day: 'numeric' }) : null;
  const searchSummary = searchDone
    ? `${lastSearch.recommended_count != null ? `${lastSearch.recommended_count} recommended of ` : ''}${lastSearch.results_count || 0} judged${searchWhen ? ` · ${searchWhen}` : ''}`
    : null;
  const findBtn = sellerSide && window.BuyerSearchDrawer && !window.AtlasIsGuest ? (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      {searchSummary && <span style={{ fontSize: 11, color: 'var(--text-3)' }}>Buyer search: {searchSummary}</span>}
      {lastSearch !== undefined && (
        <FindBuyersButton deal={deal} search={lastSearch} onStarted={loadLastSearch} onOpen={() => setSearchOpen(true)} />
      )}
    </span>
  ) : null;
  // "+ Add buyer": one firm by hand — the buyer that just signed the NDA
  // (window.AddBuyerToDealDrawer, views/buyer_add.jsx). Find buyers ranks the
  // roster; this is for the buyer you already know about.
  const addBtn = sellerSide && onPatch && window.AddBuyerToDealDrawer ? (
    <Btn size="xs" variant="outline" icon={<Icon.Plus size={10} />} onClick={() => setAddOpen(true)}
      title="Put one buyer on this deal — NDA, stage and documents">
      Add buyer
    </Btn>
  ) : null;
  const actions = (findBtn || addBtn) ? (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>{addBtn}{findBtn}</span>
  ) : null;
  const drawer = (
    <>
      {searchOpen && window.BuyerSearchDrawer && (
        <window.BuyerSearchDrawer deal={deal} onClose={() => { setSearchOpen(false); loadLastSearch(); }}
          onAdded={() => { onBuyersAdded && onBuyersAdded(); loadLastSearch(); }} />
      )}
      {addOpen && window.AddBuyerToDealDrawer && (
        <window.AddBuyerToDealDrawer deal={{ id: deal.id, name: deal.name }}
          onClose={() => setAddOpen(false)}
          onDone={() => { setAddOpen(false); onBuyersAdded && onBuyersAdded(); }} />
      )}
    </>
  );

  if (buyerEngs.length === 0) {
    return (
      <Section title="Interested buyers · 0" action={actions} collapsible id="deal.buyers"
        preview={searchSummary ? `none on the tracker yet · buyer search: ${searchSummary}` : 'none linked yet'}>
        <div style={{ padding: '14px', fontSize: 12, color: 'var(--text-3)', lineHeight: 1.6 }}>
          {searchDone ? (
            <>
              No buyers on the tracker yet, but a buyer search{searchWhen ? ` on ${searchWhen}` : ''} ranked the roster for this deal:
              {' '}<b style={{ color: 'var(--text-2)' }}>{searchSummary}</b>.{' '}
              <button onClick={() => setSearchOpen(true)} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit', color: 'var(--accent-text)', fontWeight: 600 }}>
                View results
              </button>
              {' '}and add the ones to blast.
            </>
          ) : (
            <>
              No interested buyers linked yet
              {addBtn ? ' — add one with Add buyer' : ''}
              {findBtn ? `${addBtn ? ',' : ' —'} or run Find buyers to rank the roster for this deal.` : '.'}
            </>
          )}
        </div>
        {drawer}
      </Section>
    );
  }

  const passed = buyerEngs.filter((b) => b.interest_level === 'passed').length;
  const live   = buyerEngs.length - passed;
  const statusPred = (BE_STATUS_FILTERS.find(([k]) => k === filter.status) || [])[2];
  const byStatus = statusPred ? buyerEngs.filter(statusPred) : buyerEngs;
  const shown = filter.nda ? byStatus.filter(beNdaSigned) : byStatus;
  // Each chip counts what clicking it would show, given the other filter.
  const ndaPool = filter.nda ? buyerEngs.filter(beNdaSigned) : buyerEngs;
  return (
    <Section title={`Interested buyers · ${buyerEngs.length}`} collapsible id="deal.buyers"
      preview={`${live} live · ${passed} passed${searchSummary ? ` · buyer search: ${searchSummary}` : ''}`} action={actions}>
      {drawer}
      <div style={{ padding: '8px 14px', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', borderBottom: '1px solid var(--border)' }}>
        {BE_STATUS_FILTERS.map(([k, label, p]) => (
          <BeChip key={k} on={filter.status === k} onClick={() => setFilter({ status: k })}
            label={label} count={p ? ndaPool.filter(p).length : ndaPool.length} />
        ))}
        <span style={{ width: 1, height: 16, background: 'var(--border)', margin: '0 4px' }} />
        <BeChip on={filter.nda} onClick={() => setFilter({ nda: !filter.nda })}
          label="NDA signed" count={byStatus.filter(beNdaSigned).length} />
      </div>
      <BuyerBoardTable deal={deal} rows={shown} onPatch={onPatch} onRowUpdate={onRowUpdate} />
    </Section>
  );
};

Object.assign(window, { BuyerEngagementsSection, PursuitStageCell, BE_FILTER_KEY, BE_STATUS_FILTERS });

// ============ The board table (classic Section + the Buyers workspace) ============
// Header + rows of the buyer board. BuyerEngagementsSection renders it with
// deal/rows/onPatch/onRowUpdate only, which reproduces its earlier markup
// exactly; the Buyers workspace (views/buyers_workspace.jsx) adds `wide`
// (CIM / IOI / LOI columns), `select` (the bulk checkbox column), `focusId`
// (one highlighted row), `empty` (what an empty filter shows) and `guest`
// (no links out of a deal guest's access: plain names, plain stage pills).
const BE_WIDE_COL = { select: 34, cim: 112, ioi: 96, loi: 112 };
const beShortDate = (iso) => (iso ? (([y, m, d]) => `${m}/${d}/${y.slice(2)}`)(String(iso).slice(0, 10).split('-')) : null);
const BuyerBoardTable = ({ deal, rows, onPatch, onRowUpdate, wide = false, select = null, focusId = null, empty = 'No buyers match.', guest = false }) => {
  const EditableCell = window.EditableCell;
  const DoneDate = window.OutreachDoneDate;
  const minW = BE_MIN_W + (wide ? BE_WIDE_COL.cim + BE_WIDE_COL.ioi + BE_WIDE_COL.loi : 0) + (select ? BE_WIDE_COL.select : 0);
  // Same keys, same order as the classic row's style literal.
  const rowStyle = { display: 'flex', minHeight: 36, alignItems: 'center', borderBottom: '1px solid var(--border)' };
  const wideHead = (label, w) => (
    <div style={{ width: w, flexShrink: 0, padding: '0 10px', display: 'flex', alignItems: 'center' }}>{label}</div>
  );
  const allOn = !!select && rows.length > 0 && rows.every((b) => select.ids.has(b.id));
  const someOn = !!select && rows.some((b) => select.ids.has(b.id));
  const today = () => window.localISODate();
  return (
    <div style={{ overflowX: 'auto' }}>
      <div style={{ minWidth: minW }}>
      <div style={{ display: 'flex', height: 28, background: 'var(--surface-2)', fontSize: 10.5, fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '1px solid var(--border)' }}>
        {select && (
          <div style={{ width: BE_WIDE_COL.select, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <input type="checkbox" aria-label="Select all" checked={allOn} disabled={select.disabled || !rows.length}
              ref={(el) => { if (el) el.indeterminate = someOn && !allOn; }}
              onChange={() => select.setAll(allOn ? [] : rows.map((b) => b.id))}
              style={{ margin: 0, accentColor: 'var(--accent)' }} />
          </div>
        )}
        <div style={{ flex: 1.4, minWidth: BE_BUYER_MIN, padding: '0 14px', display: 'flex', alignItems: 'center' }}>Buyer</div>
        <div style={{ width: BE_COL.pursuit, flexShrink: 0, padding: '0 10px', display: 'flex', alignItems: 'center' }}>Pursuit</div>
        <div style={{ width: BE_COL.blast, flexShrink: 0, padding: '0 10px', display: 'flex', alignItems: 'center' }}>Blast</div>
        <div style={{ width: BE_COL.nda, flexShrink: 0, padding: '0 10px', display: 'flex', alignItems: 'center' }}>NDA</div>
        {wide && wideHead('CIM', BE_WIDE_COL.cim)}
        {wide && wideHead('IOI', BE_WIDE_COL.ioi)}
        {wide && wideHead('LOI', BE_WIDE_COL.loi)}
        <div style={{ width: BE_COL.interest, flexShrink: 0, padding: '0 10px', display: 'flex', alignItems: 'center' }}>Interest</div>
        <div style={{ width: BE_COL.added, flexShrink: 0, padding: '0 10px', display: 'flex', alignItems: 'center' }}>Added</div>
        <div style={{ width: BE_COL.bsCall, flexShrink: 0, padding: '0 10px', display: 'flex', alignItems: 'center' }}>BS Call</div>
      </div>
      {!rows.length && (
        <div style={{ padding: '12px 14px', fontSize: 12, color: 'var(--text-3)' }}>{empty}</div>
      )}
      {rows.map((b) => (
        <div key={b.id} data-be-row={wide ? b.id : undefined}
          style={wide ? { ...rowStyle, background: focusId === b.id ? 'var(--accent-soft)' : undefined, transition: 'background .6s' } : rowStyle}>
          {select && (
            <div style={{ width: BE_WIDE_COL.select, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <input type="checkbox" checked={select.ids.has(b.id)} disabled={select.disabled}
                aria-label={`Select ${b.buyer_name || 'buyer'}`} onChange={() => select.toggle(b.id)}
                style={{ margin: 0, accentColor: 'var(--accent)' }} />
            </div>
          )}
          <div style={{ flex: 1.4, minWidth: BE_BUYER_MIN, padding: '6px 14px', fontSize: 12 }}>
            {guest ? <span style={{ color: 'var(--text)', fontWeight: 500 }}>{b.buyer_name || '—'}</span> : (
              <span
                onClick={() => window.AtlasNav?.goToCompany?.(b.buyer_company_id)}
                title={[
                  `Open ${b.buyer_name || 'buyer'}`,
                  (b.buyer_focus || []).length ? `Focus: ${b.buyer_focus.slice(0, 6).join(', ')}${b.buyer_focus.length > 6 ? '…' : ''}` : null,
                  b.buyer_min_ebitda || b.buyer_max_ebitda ? `EBITDA ${b.buyer_min_ebitda ? '$' + b.buyer_min_ebitda / 1e6 + 'M' : '?'}–${b.buyer_max_ebitda ? '$' + b.buyer_max_ebitda / 1e6 + 'M' : '?'}` : null,
                ].filter(Boolean).join('\n')}
                onMouseOver={(e) => { e.currentTarget.style.textDecoration = 'underline'; }}
                onMouseOut={(e) => { e.currentTarget.style.textDecoration = 'none'; }}
                style={{ color: 'var(--text)', fontWeight: 500, cursor: 'pointer', textUnderlineOffset: 2 }}
              >{b.buyer_name || b.buyer_company_id}</span>
            )}
            {b.loi_submitted && <span style={{ marginLeft: 6 }}><Pill hue="blue" sz="sm">LOI</Pill></span>}
            {b.buyer_total_deals > 1 && (
              <span style={{ marginLeft: 6, fontSize: 10.5, color: 'var(--text-4)' }} title="Deals this buyer is engaged on">· {b.buyer_total_deals} deals</span>
            )}
          </div>
          {/* The buyer's funnel position = its pursuit record's DealCloud
              stage (dc_stage, 050). Editable: the pick writes back to DC
              and keeps Interest in step (Lost = passed); a buyer with no
              pursuit yet gets one. ↗ opens the pursuit record. */}
          <div style={{ width: BE_COL.pursuit, flexShrink: 0, padding: '0 10px', display: 'flex', alignItems: 'center', minWidth: 0, overflow: 'hidden' }}>
            {guest
              ? (b.pursuit_stage
                ? <Pill hue={window.dcStageHue(b.pursuit_stage)} sz="sm" maxW={BE_COL.pursuit - 48}>{window.cleanDcStage(b.pursuit_stage)}</Pill>
                : <span style={{ color: 'var(--text-4)' }}>—</span>)
              : <PursuitStageCell b={b} dealName={deal?.name} editable={!!onPatch} maxW={BE_COL.pursuit - 48}
                  onSaved={(row) => onRowUpdate && onRowUpdate(b.id, row)} />}
          </div>
          <div style={{ width: BE_COL.blast, flexShrink: 0, padding: '0 10px', display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }} title={b.blast_type === 'pb' ? 'Preferred Buyer' : b.blast_type === 'peg' ? 'Private Equity Group (PEG blast)' : undefined}>
            <EditableCell type="select" value={b.blast_type} editable={!!onPatch}
              options={[
                { value: 'pb',  label: 'PB',  hue: 'violet' },
                { value: 'peg', label: 'PEG', hue: 'blue' },
                { value: '',    label: '— clear —' },
              ]}
              onSave={(v) => onPatch(b.id, { blast_type: v })}
              displayAs={b.blast_type ? <Pill hue={b.blast_type === 'pb' ? 'violet' : 'blue'} sz="sm">{b.blast_type.toUpperCase()}</Pill> : undefined}
            />
            <EditableCell type="date" value={b.blast_sent_date} editable={!!onPatch}
              onSave={(v) => onPatch(b.id, { blast_sent_date: v })}
              displayAs={b.blast_sent_date ? <span style={{ fontSize: 10, color: 'var(--text-4)', whiteSpace: 'nowrap' }} title={`Blast sent ${b.blast_sent_date}`}>{(([y, m, d]) => `${m}/${d}/${y.slice(2)}`)(b.blast_sent_date.split('-'))}</span> : undefined}
            />
          </div>
          <div style={{ width: BE_COL.nda, flexShrink: 0, padding: '3px 10px', display: 'flex', flexDirection: 'column', gap: 1, justifyContent: 'center', alignItems: 'flex-start' }}>
            <EditableCell type="select" value={b.nda_status || 'not_sent'} editable={!!onPatch}
              options={['not_sent', 'sent', 'redlined', 'signed', 'declined'].map((v) => ({ value: v, label: v.replace('_', ' '), hue: NDA_HUE[v] }))}
              onSave={(v) => onPatch(b.id, { nda_status: v })}
              displayAs={<Pill hue={NDA_HUE[b.nda_status] || 'slate'} sz="sm">{(b.nda_status || 'not_sent').replace('_', ' ')}</Pill>}
            />
            {/* When it was signed — the date the outreach tracker and the
                add-a-buyer form record, editable here too. */}
            {b.nda_status === 'signed' && (
              <EditableCell type="date" value={b.nda_signed_date} editable={!!onPatch}
                onSave={(v) => onPatch(b.id, { nda_signed_date: v })}
                displayAs={b.nda_signed_date
                  ? <span style={{ fontSize: 10, color: 'var(--text-4)', whiteSpace: 'nowrap' }} title={`NDA signed ${b.nda_signed_date}`}>{(([y, m, d]) => `${m}/${d}/${y.slice(2)}`)(b.nda_signed_date.split('-'))}</span>
                  : <span style={{ fontSize: 10, color: 'var(--text-4)' }}>+ date</span>}
              />
            )}
          </div>
          {wide && (
            <div style={{ width: BE_WIDE_COL.cim, flexShrink: 0, padding: '3px 10px', display: 'flex', alignItems: 'center' }}>
              {DoneDate && (
                <DoneDate done={b.cim_sent} date={b.cim_sent_date} doneLabel="sent" editable={!!onPatch}
                  tickTitle="Mark the CIM as sent today" untickTitle="CIM sent — untick to clear"
                  onTick={(on) => onPatch(b.id, on ? { cim_sent: true, cim_sent_date: b.cim_sent_date || today() } : { cim_sent: false, cim_sent_date: null })}
                  onDate={(v) => onPatch(b.id, { cim_sent_date: v })} />
              )}
            </div>
          )}
          {wide && (
            <div style={{ width: BE_WIDE_COL.ioi, flexShrink: 0, padding: '3px 10px', display: 'flex', alignItems: 'center', fontSize: 11.5 }}>
              <EditableCell type="date" value={b.ioi_date} editable={!!onPatch}
                onSave={(v) => onPatch(b.id, { ioi_date: v })}
                displayAs={b.ioi_date ? <span className="num">{beShortDate(b.ioi_date)}</span> : undefined} />
            </div>
          )}
          {wide && (
            <div style={{ width: BE_WIDE_COL.loi, flexShrink: 0, padding: '3px 10px', display: 'flex', alignItems: 'center' }}>
              {DoneDate && (
                <DoneDate done={b.loi_submitted} date={b.loi_date} doneLabel="yes" editable={!!onPatch}
                  tickTitle="Mark an LOI received today" untickTitle="LOI received — untick to clear"
                  onTick={(on) => onPatch(b.id, on ? { loi_submitted: true, loi_date: b.loi_date || today() } : { loi_submitted: false, loi_date: null })}
                  onDate={(v) => onPatch(b.id, { loi_date: v })} />
              )}
            </div>
          )}
          <div style={{ width: BE_COL.interest, flexShrink: 0, padding: '3px 10px', display: 'flex', flexDirection: 'column', gap: 1, justifyContent: 'center', alignItems: 'flex-start' }}>
            <EditableCell type="select" value={b.interest_level} editable={!!onPatch}
              options={[
                { value: 'high',   label: 'high',   hue: 'green' },
                { value: 'medium', label: 'medium', hue: 'amber' },
                { value: 'low',    label: 'low',    hue: 'amber' },
                { value: 'passed', label: 'passed', hue: 'rose' },
                { value: '',       label: '— clear —' },
              ]}
              onSave={(v) => onPatch(b.id, { interest_level: v })}
              displayAs={b.interest_level ? <Pill hue={b.interest_level === 'passed' ? 'rose' : b.interest_level === 'high' ? 'green' : 'amber'} sz="sm">{b.interest_level}</Pill> : undefined}
            />
            {b.interest_level === 'passed' && (
              /* Full width of the cell: the reason used to be squeezed into
                 the pill's shrink-to-fit box and read "reas…". */
              <div style={{ width: '100%', minWidth: 0 }}>
                <PassReasonCell b={b} onPatch={onPatch} />
              </div>
            )}
          </div>
          {/* When the buyer's pursuit was opened, and when it last changed.
              DealCloud owns both (dc_created_at/dc_modified_at on the pursuit
              shell); for the few rows with no shell yet we fall back to when
              Atlas derived the roster row and say so in the tooltip. */}
          {(() => {
            const dcCreated = b.pursuit_created_at;
            const created   = dcCreated || b.roster_created_at;
            const modified  = b.pursuit_modified_at;
            const showMod   = modified && fmtRosterDate(modified) !== fmtRosterDate(created);
            return (
              <div
                title={created ? [
                  dcCreated
                    ? `Pursuit created ${fmtRosterDateLong(created)} (DealCloud)`
                    : `Added to the Atlas roster ${fmtRosterDateLong(created)} — no DealCloud pursuit linked yet`,
                  modified ? `Last modified ${fmtRosterDateLong(modified)} (DealCloud)` : null,
                ].filter(Boolean).join('\n') : undefined}
                style={{ width: BE_COL.added, flexShrink: 0, padding: '3px 10px', display: 'flex', flexDirection: 'column', gap: 1, justifyContent: 'center', fontSize: 11.5, color: 'var(--text-3)' }}
              >
                {created
                  ? <span style={{ whiteSpace: 'nowrap', color: dcCreated ? 'var(--text-3)' : 'var(--text-4)' }}>{fmtRosterDate(created)}</span>
                  : <span style={{ color: 'var(--text-4)' }}>—</span>}
                {showMod && (
                  <span style={{ fontSize: 10, color: 'var(--text-4)', whiteSpace: 'nowrap' }}>mod {fmtRosterDate(modified)}</span>
                )}
              </div>
            );
          })()}
          <div style={{ width: BE_COL.bsCall, flexShrink: 0, padding: '0 10px', fontSize: 11.5, color: 'var(--text-3)' }}>
            <EditableCell type="date" value={b.bs_call_date} editable={!!onPatch}
              onSave={(v) => onPatch(b.id, { bs_call_date: v })}
            />
          </div>
        </div>
      ))}
      </div>
    </div>
  );
};


// ============ Record-page cards (step 5; v2 only) ============
// The page slots mount these only when window.recordCardsOn('<Name>') is true
// (views/record_summary.jsx): surface 'v2' and the card on window. Plain
// function components (recordCardsOn tests typeof === 'function').
const BUYERS_DOT = { display: 'inline-block', width: 7, height: 7, borderRadius: 999, marginLeft: 6, verticalAlign: 'middle', background: 'var(--amber-fg, #b54708)' };
const buyersDim = (v) => <span style={{ color: 'var(--text-4)' }}>{v}</span>;
// A count chip; 0 (or not known yet) is dimmed.
// `inert` (the phone's read-only card: no Buyers workspace there): a display chip, not a button.
const BuyersCountChip = ({ label, n, onClick, inert = false }) => {
  const Tag = inert ? 'span' : 'button';
  return (
    <Tag {...(inert ? null : { type: 'button', onClick: onClick || undefined, disabled: !onClick })}
      style={{ padding: '3px 9px', fontSize: 11.5, borderRadius: 999, fontFamily: 'inherit', cursor: onClick ? 'pointer' : 'default',
        border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text-2)', opacity: n ? 1 : 0.55 }}>
      {label} <span className="num" style={{ color: 'var(--text-4)' }}>{n == null ? '—' : n}</span>
    </Tag>
  );
};

// Find buyers, out on the page (11-02 huddle: it sat in the card's ⋯ and a run
// started there went unnoticed). One click runs the search on every source;
// while it runs the button shows the step and starts nothing (the API also
// hands back a run already going: buyer_search.start_search `already`).
// `search` is the latest run (the record summary's buyer_search or a
// listBuyerSearches row); onStarted refetches it, onOpen opens the drawer.
const BUYER_SEARCH_SOURCES = 'Atlas buyer roster, imported buyer lists, regional strategics and strategic discovery (Apollo)';
if (typeof document !== 'undefined' && !document.getElementById('atlas-buyers-spin')) {
  const el = document.createElement('style');
  el.id = 'atlas-buyers-spin';
  el.textContent = '@keyframes atlas-spin { to { transform: rotate(360deg); } }';
  document.head.appendChild(el);
}
// A started run `search` hasn't caught up with by now (a refetch that failed)
// turns into a failure the user can retry, not a spinner forever.
const FIND_PENDING_MS = 30000;
const FIND_SR_ONLY = { position: 'absolute', width: 1, height: 1, padding: 0, margin: -1, overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: 0 };
const FindBuyersButton = ({ deal, search, onStarted, onOpen }) => {
  const [starting, setStarting] = React.useState(false);
  const [pending, setPending] = React.useState(null);   // the run this click started, until `search` shows it
  const sid = search && search.id;
  React.useEffect(() => { if (pending && sid === pending.id) setPending(null); }, [sid, pending]);
  React.useEffect(() => {
    if (!pending || pending.status === 'failed') return undefined;
    const t = setTimeout(() => setPending((p) => (p && p.id === pending.id
      ? { ...p, status: 'failed', error: 'Started, but its progress did not load. Reload the page to follow it.' } : p)), FIND_PENDING_MS);
    return () => clearTimeout(t);
  }, [pending]);
  const s = pending || search;
  const status = s && s.status;
  const running = status === 'queued' || status === 'running';
  const busy = React.useRef(false);                     // a double click lands before `starting` re-renders
  const start = async () => {
    if (busy.current || running) return;
    busy.current = true;
    setStarting(true);
    try {
      const r = await window.AtlasApi.startBuyerSearch(deal.id, { regional: true, strategic: true });
      if (r && r.id && r.id !== sid) setPending({ id: r.id, status: 'queued', step: null });
      if (onStarted) onStarted(r);
    } catch (e) {
      pushToast({ type: 'error', title: 'Could not start the buyer search', body: String(e.message || e) });
    } finally {
      busy.current = false;
      setStarting(false);
    }
  };
  // Screen readers hear a change, not the page load: the live region mounts
  // empty and only gets text when the status moves.
  const [news, setNews] = React.useState('');
  const heard = React.useRef(status);
  React.useEffect(() => {
    if (heard.current === status) return;
    heard.current = status;
    setNews(running ? 'Searching all sources' : status === 'done' ? 'Buyer search done' : status === 'failed' ? 'Buyer search failed' : '');
  }, [status, running]);
  let still = false;
  try { still = window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (_) { /* no matchMedia */ }
  const spin = running || starting;
  const step = running ? (window.BUYER_SEARCH_STEP_LABEL || {})[s.step] : null;
  const open = !starting && (running || status === 'done');
  // One button in one place through every state, never disabled: a remount or
  // a disabled button drops keyboard focus (busy guards the repeat clicks).
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, minWidth: 0, flexWrap: 'wrap' }}>
      {status === 'failed' && (
        <span title={s.error || undefined} style={{ fontSize: 11, color: 'var(--rose-fg)' }}>Buyer search failed</span>
      )}
      <Btn size="xs" variant={running ? 'outline' : 'primary'} onClick={open ? onOpen : start}
        title={running ? `Searching the ${BUYER_SEARCH_SOURCES} · open to follow`
          : status === 'done' ? 'Open the ranked buyers and who to call' : `Searches the ${BUYER_SEARCH_SOURCES}`}
        style={running ? { color: 'var(--accent-text)' } : undefined}
        icon={spin ? <span aria-hidden="true" style={{ width: 9, height: 9, borderRadius: '50%',
          border: `1.5px solid ${running ? 'var(--accent)' : 'currentColor'}`, borderTopColor: 'transparent', display: 'inline-block',
          flexShrink: 0, animation: still ? 'none' : 'atlas-spin 0.9s linear infinite' }} />
          : status === 'done' ? null : <Icon.Search size={11} />}>
        {starting ? 'Starting…' : running ? 'Searching all sources…' : status === 'done' ? 'View results'
          : status === 'failed' ? 'Run again' : 'Find buyers'}
      </Btn>
      {step && <span style={{ fontSize: 11, color: 'var(--text-3)' }}>{step}</span>}
      <span role="status" aria-live="polite" style={FIND_SR_ONLY}>{news}</span>
    </span>
  );
};

// The deal's Buyers card: counts, the buyer-search line, the outreach line and
// the 3 latest-changed buyers, all from the record summary (no board or
// tracker fetch on mount). Every click opens the Buyers workspace
// (?panel=buyers). Drawers sit outside the Section: a folded Section renders
// no children, and the header's Add / Find must still open them.
// readOnly (the phone deal page, step 15): counts, the search and outreach lines and the
// latest buyers as text: no ⋯, no View results, no Link campaign / Upload list, no drawers.
// sectionId: its own fold key on the phone (phone.deal.buyers), never the desktop's.
const DealBuyersCard = ({ deal, variant = 'card', readOnly = false, sectionId = 'deal.buyers' }) => {
  const sum = window.useRecordSummary('deal', deal && deal.id);
  const [adding, setAdding] = React.useState(false);
  const [searching, setSearching] = React.useState(false);
  const [linking, setLinking] = React.useState(false);
  const [importing, setImporting] = React.useState(false);
  if (!deal || deal.type === 'buyer_buyside') return null;
  const kit = window.BuyersKit;
  if (!kit) return null;
  const guest = !!window.AtlasIsGuest;
  const canWrite = !readOnly && window.recordCanWrite();
  const s = sum.data || {};
  const b = s.buyers || null;                       // null while loading, or a failed block
  const bs = guest ? null : (s.buyer_search || null);
  const o = guest ? null : (s.outreach || null);
  const canOpen = window.recordPanelAvailable('buyers', window.recordSurface());
  const inert = !canOpen;                           // a phone: no workspace, so nothing here is a button
  // A line that opens the workspace (a button), or the same line as text where there is none.
  const line = (onClick, style, children, key) => (inert
    ? <div key={key} style={{ ...style, font: undefined, fontFamily: 'inherit', cursor: 'default' }}>{children}</div>
    : <button key={key} type="button" onClick={onClick} style={style}>{children}</button>);
  const open = (params) => { window.openRecordPanel('buyers', params, { via: 'button' }); };
  const changed = () => { if (window.notifyBuyersChanged) window.notifyBuyersChanged(deal.id, 'deal-card'); };
  const lineStyle = { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 11.5, color: 'var(--text-3)', minWidth: 0 };
  const linkStyle = { background: 'none', border: 'none', padding: 0, font: 'inherit', color: 'inherit', textAlign: 'left',
    cursor: canOpen ? 'pointer' : 'default', display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' };
  const title = (
    <span>Buyers · {b ? b.total : buyersDim('—')}
      {!guest && b && b.waiting_on_us > 0 && (
        <span role="img" aria-label={`Waiting on us · ${b.waiting_on_us}`} title={`Waiting on us · ${b.waiting_on_us}`} style={BUYERS_DOT} />
      )}
    </span>
  );
  // A side-column card (240–440px): ⤢ and ⋯ in the header. Find buyers is a
  // button in the body (FindBuyersButton); ⋯ opens its drawer for exclusions
  // and imported lists. The Buyers workspace carries both as labelled buttons.
  const findable = canWrite && !guest && window.BuyerSearchDrawer && !!sum.data;   // once the latest run is known
  const more = [
    findable && { key: 'find', label: 'Buyer search settings', onSelect: () => setSearching(true) },
    canWrite && window.AddBuyerToDealDrawer && { key: 'add', label: 'Add buyer', onSelect: () => setAdding(true) },
  ];
  const actions = (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      {!b && sum.error && <Btn size="xs" variant="ghost" title="Retry" icon={<Icon.Refresh size={11} />} onClick={() => sum.refresh()} />}
      {canOpen && <Btn size="xs" variant="ghost" title="Open Buyers" icon={<Icon.Maximize size={11} />} onClick={() => open({ tab: 'board' })} />}
      {window.RecordMenu && !readOnly && <window.RecordMenu items={more} />}
    </span>
  );
  const drawers = (
    <>
      {adding && window.AddBuyerToDealDrawer && (
        <window.AddBuyerToDealDrawer deal={{ id: deal.id, name: deal.name }} onClose={() => setAdding(false)}
          onDone={() => { setAdding(false); changed(); }} />
      )}
      {searching && window.BuyerSearchDrawer && (
        <window.BuyerSearchDrawer deal={deal} onClose={() => setSearching(false)} onAdded={changed} />
      )}
      {linking && window.OutreachCampaignDrawer && (
        <window.OutreachCampaignDrawer deal={deal} onClose={() => setLinking(false)} onLinked={() => sum.refresh()} />
      )}
      {importing && window.OutreachImportDrawer && (
        <window.OutreachImportDrawer deal={deal} open onClose={() => setImporting(false)} onDone={changed} />
      )}
    </>
  );
  return (
    <>
      <Section variant={variant} title={title} collapsible id={sectionId} preview={b ? kit.cardPreview(b) : '—'} action={actions}>
        <div style={{ padding: '10px 14px', display: 'flex', flexDirection: 'column', gap: 9 }}>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {kit.CHIPS.map((c) => (
              <BuyersCountChip key={c.status} label={c.label} n={b ? (b[c.key] || 0) : null} inert={inert}
                onClick={canOpen ? () => open({ tab: 'board', status: c.status }) : null} />
            ))}
          </div>
          {(findable || (bs && (bs.running || bs.status === 'done'))) && (
            <div style={lineStyle}>
              {bs && !bs.running && bs.status === 'done' && line(canOpen ? () => open({ tab: 'board' }) : undefined, linkStyle,
                `Buyer search: ${kit.searchSummary(bs)}`)}
              {findable ? (
                <FindBuyersButton deal={deal} search={bs} onStarted={() => sum.refresh()} onOpen={() => setSearching(true)} />
              ) : bs && bs.running && <Pill hue="blue" sz="sm">Search running…</Pill>}
            </div>
          )}
          {!guest && (o && o.tracking ? (
            line(canOpen ? () => open({ tab: 'outreach' }) : undefined, { ...linkStyle, ...lineStyle }, (<>
              <span style={{ fontWeight: 600, color: 'var(--text-2)' }}>Outreach</span>
              <span className="num">{kit.outreachLine(o.kpis)}</span>
              {(o.campaigns || []).map((c) => (
                <span key={c.id || c.campaign_id} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '1px 7px',
                  borderRadius: 999, border: '1px solid var(--border)', background: 'var(--surface-2)', color: 'var(--text-2)' }}>
                  <Icon.Mail size={10} /> {c.name || c.campaign_id}
                </span>
              ))}
              {o.next_deadline && <Pill hue={o.next_deadline.days <= 3 ? 'amber' : 'slate'} sz="sm">{kit.deadlineChip(o.next_deadline)}</Pill>}
            </>))
          ) : (
            <div style={lineStyle}>
              {line(canOpen ? () => open({ tab: 'outreach' }) : undefined, { ...linkStyle, fontWeight: 600, color: 'var(--text-2)' }, 'Outreach')}
              {!o && buyersDim('—')}
              {o && deal.type === 'sell_side' && canWrite && window.OutreachCampaignDrawer && (
                <Btn size="xs" variant="outline" icon={<Icon.Mail size={11} />} onClick={() => setLinking(true)}>Link campaign</Btn>
              )}
              {o && deal.type === 'sell_side' && canWrite && window.OutreachImportDrawer && (
                <Btn size="xs" variant="outline" icon={<Icon.Upload size={11} />} onClick={() => setImporting(true)}>Upload list</Btn>
              )}
            </div>
          ))}
        </div>
        {b && (b.latest || []).length > 0 && (
          <div>
            {b.latest.map((r) => line(canOpen ? () => open({ tab: 'board', focus: r.id }) : undefined,
              { display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '7px 14px', border: 'none',
                borderTop: '1px solid var(--border)', background: 'transparent', font: 'inherit', textAlign: 'left',
                cursor: canOpen ? 'pointer' : 'default' }, (<>
                <span style={{ flex: 1, minWidth: 0, fontSize: 12, fontWeight: 500, color: 'var(--text)', overflow: 'hidden',
                  textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.buyer_name || '—'}</span>
                {r.pursuit_stage
                  ? <Pill hue={window.dcStageHue(r.pursuit_stage)} sz="sm" maxW={150}>{window.cleanDcStage(r.pursuit_stage)}</Pill>
                  : buyersDim('—')}
              </>), r.id))}
          </div>
        )}
      </Section>
      {!readOnly && drawers}
    </>
  );
};

// The company's Buyers card (the right rail, in place of InterestedBuyersPanel;
// same props): per seller deal, its line and up to 10 buyers (live first),
// each with an editable pursuit stage; 'All N' opens that deal's Buyers
// workspace. Fed by the page's roster rows, not the summary.
const CompanyBuyersCard = ({ buyers, dealNames, onOpenDeal, onRowUpdate, sectionId = 'company.interested_buyers', variant = 'rail' }) => {
  if (!buyers || buyers.length === 0) return null;            // same as classic
  const kit = window.BuyersKit;
  const PursuitStageCell = window.PursuitStageCell;
  if (!kit || !PursuitStageCell) return null;
  const canWrite = window.recordCanWrite();
  const names = dealNames || {};
  const groups = kit.groupByDeal(buyers, 10, Object.keys(names));
  const all = kit.counts(buyers);
  const intHue = { high: 'green', medium: 'amber', low: 'slate', passed: 'rose' };
  return (
    <Section variant={variant} title={`Buyers · ${buyers.length}`} collapsible id={sectionId}
      preview={`${all.live} live · ${all.passed} passed`}>
      {groups.map((g, gi) => (
        <div key={g.engagementId}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px 6px', fontSize: 11, color: 'var(--text-3)',
            borderTop: gi ? '1px solid var(--border)' : 'none' }}>
            <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              <span onClick={onOpenDeal ? () => onOpenDeal(g.engagementId) : undefined}
                style={{ fontWeight: 600, color: 'var(--text-2)', cursor: onOpenDeal ? 'pointer' : 'default' }}>{names[g.engagementId] || '—'}</span>
              <span className="num">: {g.live} live · {g.nda} NDA · {g.passed} passed</span>
            </span>
            <button type="button" onClick={() => window.AtlasNav?.goToDeal?.(g.engagementId, { panel: 'buyers', tab: 'board' })}
              style={{ flexShrink: 0, background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit', fontSize: 11,
                fontWeight: 600, color: 'var(--accent-text)' }}>
              All {g.total}
            </button>
          </div>
          {g.rows.map((b) => (
            <div key={b.id}
              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px', borderTop: '1px solid var(--border)' }}
              onMouseEnter={(ev) => { ev.currentTarget.style.background = 'var(--surface-2)'; }}
              onMouseLeave={(ev) => { ev.currentTarget.style.background = 'transparent'; }}>
              <div style={{ flex: 1, minWidth: 0, opacity: b.interest_level === 'passed' ? 0.6 : 1 }}>
                <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  <span onClick={() => window.AtlasNav?.goToCompany?.(b.buyer_company_id)}
                    title={`Open ${b.buyer_name || 'buyer'}`}
                    onMouseOver={(e) => { e.currentTarget.style.textDecoration = 'underline'; }}
                    onMouseOut={(e) => { e.currentTarget.style.textDecoration = 'none'; }}
                    style={{ cursor: 'pointer', textUnderlineOffset: 2 }}>{b.buyer_name || '—'}</span>
                  {b.buyer_total_deals > 1 && (
                    <span style={{ marginLeft: 6, fontSize: 10.5, fontWeight: 400, color: 'var(--text-4)' }}>· {b.buyer_total_deals} deals</span>
                  )}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-3)', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginTop: 2 }}>
                  <PursuitStageCell b={b} dealName={names[b.engagement_id]} maxW={150} editable={canWrite}
                    onSaved={(row) => onRowUpdate && onRowUpdate(b.id, row)} />
                  {b.interest_level && (
                    <span title={b.interest_level === 'passed' && b.pass_reason
                      ? `Pass reason: ${b.pass_reason === 'other' && b.pass_detail ? b.pass_detail : b.pass_reason.replace(/_/g, ' ')}` : undefined}>
                      <Pill hue={intHue[b.interest_level] || 'slate'} sz="sm">{b.interest_level}</Pill>
                    </span>
                  )}
                  {b.nda_status && b.nda_status !== 'not_sent' && <Pill hue={b.nda_status === 'signed' ? 'green' : 'amber'} sz="sm">NDA {b.nda_status}</Pill>}
                  {b.loi_submitted && <Pill hue="blue" sz="sm">LOI</Pill>}
                </div>
              </div>
              <button type="button" onClick={() => window.AtlasNav?.goToCompany?.(b.buyer_company_id)} title={`Open ${b.buyer_name || 'buyer'}`}
                style={{ background: 'transparent', border: 'none', padding: 2, cursor: 'pointer', display: 'inline-flex', ...(window.phoneHitHost ? window.phoneHitHost() : null) }}>
                {window.PhoneHit && <window.PhoneHit />}
                <Icon.ChevronR size={12} color="var(--text-4)" />
              </button>
            </div>
          ))}
        </div>
      ))}
    </Section>
  );
};

Object.assign(window, { BuyerBoardTable, BeChip, BE_PASS_REASONS: PASS_REASONS, DealBuyersCard, CompanyBuyersCard, FindBuyersButton, useLastBuyerSearch });
